import cn from 'classnames';
import H1Title from 'components/Common/H1Title/H1Title';
import SubTitle from 'components/Common/SubTitle/SubTitle';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isDesktop from 'store/app/selector/isDesktop';
import Breadcrumbs from 'components/Common/Breadcrumbs/Breadcrumbs';
import getCategories from 'store/category/selector/getCategories';
import getCurrentCategory from 'store/category/selector/getCurrentCategory';
import getFamiliesOffset from 'store/category/selector/getFamiliesOffset';
import isCategoryLoading from 'store/category/selector/isCategoryLoading';
import getDesignsFromFamilies from 'utils/family/getDesignsFromFamilies';
import useIsScrollingUp from 'hooks/useIsScrollingUp';
import { Store } from 'store';
import getCategoryId from 'store/category/selector/getCategoryId';
import { getIsStudioSelectionCategory } from 'store/app/selector/getConfigurationByKey';
import MobileOnly from 'components/Common/Utils/MobileOnly';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import OpButton from 'components/Common/op-ui-wrappers/OpButton/OpButton';
import getNumberOfAppliedFilters from 'store/category/selector/getNumberOfAppliedFilters';
import setCategoryPageNrOperation from 'store/category/operation/setCategoryPageNrOperation';
import updateCategoryQueryStringOperation from 'store/category/operation/updateCategoryQueryStringOperation';
import { resetCategoryFiltersAction, setCategoryRefetchFlagAction } from 'store/category/slice';
import sendGtmViewItemListOperation from 'store/category/operation/sendGtmViewItemListOperation';
import DesktopOnly from 'components/Common/Utils/DesktopOnly';
import { AppBundle } from 'api/optimalprint-sdk.d';
import RecentlyViewedBlock from '../../Common/RecentlyViewed/RecentlyViewedBlock';
import DropdownSort from '../Sidebar/DropdownSort/DropdownSort';
import CategorySeoText from './CategorySeoText/CategorySeoText';
import ItemsLoadingSpinner from './ItemsLoadingSpinner/ItemsLoadingSpinner';
import LoadMoreItems from './LoadMoreItems/LoadMoreItems';
import LoadMoreItemsBefore from './LoadMoreItemsBefore/LoadMoreItemsBefore';
import NoFilteringResults from './NoFilteringResults/NoFilteringResults';
import ProductItem from './ProductItem/ProductItem';
import styles from './ItemsList.module.scss';
import FiltersDrawer from '../Sidebar/Filters/FiltersDrawer';
import Filters from '../Sidebar/Filters/Filters';
import CategoriesList from '../Sidebar/CategoriesList/CategoriesList';

export type ItemsListProps = {
  onToggleFilters: () => void;
  isFiltersOpen: boolean;
};

const ItemsList: FC<ItemsListProps> = ({ onToggleFilters, isFiltersOpen }) => {
  const isDesktopDevice = useSelector(isDesktop);
  const dispatch = useDispatch();
  const history = useHistory();
  const isLoading = useSelector(isCategoryLoading);
  const categories = useSelector(getCategories);
  const currentCategory = useSelector(getCurrentCategory);
  const familiesOffset = useSelector(getFamiliesOffset);
  const isScrollingUp = useIsScrollingUp();
  const ref = useRef<HTMLDivElement>(null);
  const categoryId = useSelector(getCategoryId);
  const isStudioSelection = useSelector((store: Store) => getIsStudioSelectionCategory(store, Number(categoryId)));
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showCollectionsDrawer, setShowCollectionsDrawer] = useState(false);
  const numberOfAppliedFilters = useSelector(getNumberOfAppliedFilters);
  const [products, setProducts] = useState<
    AppBundle.Api.Response.Category.V3.CategoryDesignsListV3Response.Family.IDesign[]
  >(getDesignsFromFamilies(categories?.families || []));

  const { t } = useTranslation();

  const initCategoryPageReset = () => {
    dispatch(setCategoryPageNrOperation(1));
    dispatch(setCategoryRefetchFlagAction(true));
    dispatch(updateCategoryQueryStringOperation(history));
  };

  const applyFilters = () => {
    initCategoryPageReset();
    setShowMobileFilters(false);
  };

  const clearFilters = () => {
    dispatch(resetCategoryFiltersAction());
    initCategoryPageReset();
    setShowMobileFilters(false);
  };

  useEffect(() => {
    if (products.length > 0) {
      dispatch(sendGtmViewItemListOperation(products));
    }
  }, [products]);

  useEffect(() => {
    setProducts(getDesignsFromFamilies(categories?.families || []));
  }, [categories?.families]);

  if (!categories) {
    return null;
  }

  return (
    <>
      <div
        className={cn(styles.itemsWrapper, 'productsWrapper', {
          [styles.loading]: isLoading,
        })}
      >
        <Breadcrumbs />
        {/* {!isDesktopDevice && (
          <div className={styles.selectCategoryButton}>
            <SelectCategoryButton />
          </div>
        )} */}
        {/* This div is here to compensate for the missing div when position fixed */}
        <div
          style={{
            height: isScrollingUp ? ref.current?.scrollHeight : 0,
          }}
        />
        <div
          ref={ref}
          className={cn(styles.scrolled, {
            [styles.pinned]: isScrollingUp,
          })}
        >
          <div className={styles.text}>
            <H1Title>{currentCategory?.title}</H1Title>
            <SubTitle>{currentCategory?.subTitle}</SubTitle>
          </div>

          <div className={styles.loadMoreItems}>
            <LoadMoreItemsBefore />
          </div>
          <div className={styles.actionBar}>
            <DesktopOnly>
              <OpButton
                icon-right={isFiltersOpen ? 'eye-off' : 'eye'}
                size="medium"
                variant="secondary"
                onClick={onToggleFilters}
              >
                {showMobileFilters ? t('txt_hide_filters') : t('txt_show_filters')}
              </OpButton>
            </DesktopOnly>
            <MobileOnly>
              <OpButton
                size="medium"
                variant="secondary"
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                icon-left="filter"
              >
                <span className={styles.upperCase}>
                  {t('txt_filters')} {numberOfAppliedFilters ? `(${numberOfAppliedFilters})` : ''}
                </span>
              </OpButton>
              <OpButton
                size="medium"
                variant="secondary"
                onClick={() => setShowCollectionsDrawer(!showCollectionsDrawer)}
              >
                <span className={styles.upperCase}>Collections</span>
              </OpButton>
            </MobileOnly>
            {isDesktopDevice && <DropdownSort />}
          </div>
          <FiltersDrawer
            visible={showMobileFilters}
            onClose={() => setShowMobileFilters(!showMobileFilters)}
            onShow={applyFilters}
            onClear={clearFilters}
            showActionButtons
            title={t('txt_filters')}
            fixedHeight
          >
            <Filters showTitle={false} applyFiltersImmediately={false} showSorting />
          </FiltersDrawer>

          <FiltersDrawer
            visible={showCollectionsDrawer}
            onClose={() => setShowCollectionsDrawer(!showCollectionsDrawer)}
            title={t('txt_collections')}
          >
            <CategoriesList />
          </FiltersDrawer>
        </div>
        <div className={styles.itemsList}>
          {products.map((product, index) => (
            <ProductItem
              key={product.designIdEncrypted}
              isReactCategory={Boolean(currentCategory?.isRenderedByReact)}
              product={product}
              index={1 + familiesOffset + index}
              trackImpression
            />
          ))}
          {isLoading && <ItemsLoadingSpinner />}
          <NoFilteringResults />
        </div>
        <div className={styles.loadMoreItems}>
          <LoadMoreItems />
        </div>
        {/* {currentCategory?.displayPreferences.displayRecentlyViewedBlock && <RecentlyViewed />} */}
        <RecentlyViewedBlock theme={isStudioSelection ? 'studio' : 'default'} />
        <CategorySeoText />
      </div>
    </>
  );
};

export default ItemsList;
